import type { Absence, AbsenceDto } from '../types';
import { selectAbsences } from './absence';

/**
 * One bar as the server resolved it, whole.
 *
 * `memberIds` is complete even where the block runs past the days that were
 * asked for, and every id in it is serialized in the same response. That is
 * what lets a click open details from what is already in hand, and what
 * removes the client's own reason to reconstruct the grouping over whatever
 * happens to be loaded.
 *
 * `anchorId` is the first member: a reference for asking about this block
 * again, not an identity that survives an edit.
 *
 * A block carries no revision. A token belongs to the thing it is submitted
 * against, and nothing is ever submitted against a bar -- see `AbsenceEntry`.
 *
 * `workdayCount` is `null` for a reader not entitled to it -- the daily
 * records withhold it on the same terms -- so render it on an explicit null
 * check rather than on falsiness. Zero is a real count.
 */
export type AbsenceBlock = {
  userId: number;
  anchorId: number;
  memberIds: number[];
  dateFrom: string;
  dateTo: string;
  allDay: boolean;
  workdayCount: number | null;
};

/**
 * One answerable thing inside a block: what an edit or a decision is scoped to.
 *
 * A block is what the calendar draws. An entry is what somebody answers, and
 * they are not the same: one drag across two different absences draws a single
 * bar and raises two change requests. An edit scoped to the bar reaches into
 * somebody else's question.
 *
 * `revision` goes back with a submission, so an edit written against a shape
 * that has since moved is refused rather than applied. It covers the entry's
 * own days and the original they replace.
 *
 * **Absent means cannot act.** Entries are sent for every block the reader may
 * act on, including where the entry is the whole block -- the ordinary case --
 * so a day with no entry is a day this reader may not edit. Reading that as
 * "the block is one entry" would widen the very edits the split exists to keep
 * apart.
 */
export type AbsenceEntry = {
  userId: number;
  anchorId: number;
  memberIds: number[];
  revision: string;
};

export type AbsenceTimelineDto = {
  blocks: AbsenceBlock[];
  entries: AbsenceEntry[];
  absences: AbsenceDto[];
};

export type AbsenceTimeline = {
  absences: Absence[];
  blocks: AbsenceBlock[];
  entries: AbsenceEntry[];
  /** Every member id of every block, so any day of a bar finds the whole. */
  blockByAbsenceId: ReadonlyMap<number, AbsenceBlock>;
  /** The same for entries, so any day finds the question it belongs to. */
  entryByAbsenceId: ReadonlyMap<number, AbsenceEntry>;
};

export const EMPTY_TIMELINE: AbsenceTimeline = {
  absences: [],
  blocks: [],
  entries: [],
  blockByAbsenceId: new Map(),
  entryByAbsenceId: new Map(),
};

/**
 * Index the projection once, on the way out of the query.
 *
 * Building the lookup here rather than per lookup keeps every consumer at one
 * map read, which is the whole point of the server sending membership: the
 * alternative was scanning the loaded rows and guessing at the edges.
 */
export function selectTimeline(dto: AbsenceTimelineDto): AbsenceTimeline {
  const blocks = dto.blocks ?? [];
  const entries = dto.entries ?? [];
  const blockByAbsenceId = new Map<number, AbsenceBlock>();
  for (const block of blocks) {
    for (const memberId of block.memberIds) {
      blockByAbsenceId.set(memberId, block);
    }
  }
  const entryByAbsenceId = new Map<number, AbsenceEntry>();
  for (const entry of entries) {
    for (const memberId of entry.memberIds) {
      entryByAbsenceId.set(memberId, entry);
    }
  }
  const absences = selectAbsences(dto.absences ?? []);
  // Stamped onto the rows, the way the daily fields already are. Every consumer
  // then reads the grouping off the row it has in hand instead of being handed
  // an index, which is what let the client keep a grouping rule of its own.
  for (const absence of absences) {
    absence.blockAnchorId = blockByAbsenceId.get(absence.id)?.anchorId;
    absence.entryAnchorId = entryByAbsenceId.get(absence.id)?.anchorId;
  }
  return {
    absences,
    blocks,
    entries,
    blockByAbsenceId,
    entryByAbsenceId,
  };
}

/**
 * The days a click on this one means.
 *
 * Its **entry** where this reader has one -- the narrowest thing the server
 * says is separately answerable, so clicking one question of a bar that holds
 * two opens that question and not its neighbour. Its **bar** otherwise, because
 * a reader with no entries is looking rather than acting, and the bar is what
 * they can see.
 *
 * Falls back to the day itself where the row carries neither, which is a row
 * that did not come from the projection. Never widened by guesswork: the
 * client's own version of this grouping is what a bar and a question came to
 * disagree about.
 */
export function absenceGroupOf(
  anchor: Absence,
  absences: readonly Absence[],
): Absence[] {
  const key = anchor.entryAnchorId ?? anchor.blockAnchorId;
  if (key == null) return [anchor];
  const field =
    anchor.entryAnchorId != null ? 'entryAnchorId' : 'blockAnchorId';
  return absences
    .filter((absence) => absence[field] === key)
    .sort((left, right) => left.dateStr.localeCompare(right.dateStr));
}

/**
 * The entry a day belongs to, or `null`.
 *
 * Null covers both "not in this response" and "not this reader's to act on",
 * deliberately alike: neither is a day this client may submit an edit for, and
 * a caller that told them apart would be guessing at the difference.
 */
export function entryOf(
  timeline: Pick<AbsenceTimeline, 'entryByAbsenceId'>,
  absenceId: number | null | undefined,
): AbsenceEntry | null {
  if (absenceId == null) return null;
  return timeline.entryByAbsenceId.get(absenceId) ?? null;
}

/**
 * The distinct entries a set of days reaches into, and the days with none.
 *
 * Distinct by identity: the map holds one object per entry, so days of one
 * question collapse to a single answer. `missing` names the ids this response
 * cannot place -- a caller must not submit while it is non-empty, because the
 * days it cannot see are days it cannot say anything about.
 */
export function entriesReaching(
  timeline: Pick<AbsenceTimeline, 'entryByAbsenceId'>,
  absenceIds: readonly number[],
): { entries: AbsenceEntry[]; missing: number[] } {
  const entries: AbsenceEntry[] = [];
  const missing: number[] = [];
  for (const absenceId of absenceIds) {
    const entry = entryOf(timeline, absenceId);
    if (!entry) {
      missing.push(absenceId);
      continue;
    }
    if (!entries.includes(entry)) entries.push(entry);
  }
  return { entries, missing };
}

/** The block a day belongs to, or `null` where it is not in this response. */
export function blockOf(
  timeline: Pick<AbsenceTimeline, 'blockByAbsenceId'>,
  absenceId: number | null | undefined,
): AbsenceBlock | null {
  if (absenceId == null) return null;
  return timeline.blockByAbsenceId.get(absenceId) ?? null;
}

/**
 * A block's or an entry's rows, in date order, from the records given.
 *
 * A member with no record is left out rather than faked. That should not
 * happen -- the projection serializes every member it names -- so a caller
 * comparing lengths is checking an invariant, not handling a normal case.
 */
export function membersOf(
  block: Pick<AbsenceBlock, 'memberIds'>,
  absences: readonly Absence[],
): Absence[] {
  const byId = new Map(absences.map((absence) => [absence.id, absence]));
  return block.memberIds
    .map((memberId) => byId.get(memberId))
    .filter((absence): absence is Absence => absence !== undefined);
}

/**
 * Whether these ids are exactly one membership, block's or entry's.
 *
 * Order does not matter; membership does. A selection that is only part of a
 * block is a narrower thing than the bar, and must not be widened to it.
 */
export function isWholeBlock(
  block: Pick<AbsenceBlock, 'memberIds'>,
  absenceIds: readonly number[],
): boolean {
  // Unique membership on both sides. Counting the list as given would read a
  // day repeated three times as the whole of a three-day bar, and widen an
  // edit from one day to all of them.
  const given = new Set(absenceIds);
  const wanted = new Set(block.memberIds);
  if (given.size !== wanted.size) return false;
  for (const id of wanted) {
    if (!given.has(id)) return false;
  }
  return true;
}
